/**
 * Admin sign-in via Clerk email OTP → Firebase custom token (same mechanism as
 * the dealer app, so there are no passwords anywhere in the system).
 *
 * Clerk emails the code and verifies it; the sync worker then exchanges the
 * Clerk session for a Firebase custom token (verifying it against Clerk's JWKS
 * server-side). Admin RIGHTS are separate: an `admins/{uid}` doc must exist —
 * the master admin's is created automatically by the worker on first sign-in.
 */
import { Clerk } from '@clerk/clerk-js';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from './firebase';
import { emulatorMode, emulatorSignIn } from './emulatorAuth';

const PUBLISHABLE_KEY: string = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '';
const SYNC_ENDPOINT: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
const CAPTCHA_ID = 'clerk-captcha';

let clerkInstance: Clerk | null = null;
let loading: Promise<Clerk> | null = null;

async function getClerk(): Promise<Clerk> {
  if (!PUBLISHABLE_KEY) {
    throw new Error('Sign-in is not configured (VITE_CLERK_PUBLISHABLE_KEY).');
  }
  if (clerkInstance?.loaded) return clerkInstance;
  if (!loading) {
    loading = (async () => {
      if (!document.getElementById(CAPTCHA_ID)) {
        const el = document.createElement('div');
        el.id = CAPTCHA_ID;
        document.body.appendChild(el);
      }
      const clerk = new Clerk(PUBLISHABLE_KEY);
      await clerk.load();
      clerkInstance = clerk;
      return clerk;
    })().catch((err: unknown) => {
      loading = null;
      throw err;
    });
  }
  return loading;
}

function clerkMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'errors' in err) {
    const first = (err as { errors: Array<{ message?: string; longMessage?: string }> }).errors?.[0];
    const msg = first?.longMessage || first?.message;
    if (msg) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Nothing here should ever hang the UI without explanation. */
async function withTimeout<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** True when Clerk says the identifier has no account yet. */
function isUnknownAccount(err: unknown): boolean {
  if (err && typeof err === 'object' && 'errors' in err) {
    const list = (err as { errors: Array<{ code?: string }> }).errors ?? [];
    return list.some((e) => e.code === 'form_identifier_not_found');
  }
  return false;
}

/**
 * Email a sign-in code.
 *
 * Tries a plain SIGN-IN first: existing accounts (every returning admin) skip
 * Clerk's bot-protection challenge entirely, which is both faster and avoids a
 * class of silent hangs. Only a genuinely new email falls back to the combined
 * sign-up flow, which may show an invisible challenge.
 */
export async function sendAdminCode(emailRaw: string): Promise<void> {
  const email = emailRaw.trim().toLowerCase();
  // Emulator: no Clerk to email anything — the code is fixed. See emulatorAuth.
  if (emulatorMode) return;
  const clerk = await withTimeout(
    getClerk(),
    20000,
    'Sign-in service is taking too long to load. Check your connection and try again.',
  );
  if (!clerk.client) throw new Error('Sign-in service failed to load. Please try again.');
  if (clerk.session) await clerk.signOut();

  const timeoutMsg = 'Could not send the code (the security check did not finish). Please try again.';
  try {
    await withTimeout(
      clerk.client.signIn.create({ strategy: 'email_code', identifier: email }),
      30000,
      timeoutMsg,
    );
    return;
  } catch (err) {
    if (!isUnknownAccount(err)) {
      throw new Error(clerkMessage(err, 'Could not send the code. Please try again.'));
    }
  }

  // New email → combined flow (creates the Clerk account, then emails the code).
  try {
    const signIn = await withTimeout(
      clerk.client.signIn.create({ strategy: 'email_code', identifier: email, signUpIfMissing: true }),
      45000,
      timeoutMsg,
    );
    if (signIn.status !== 'needs_first_factor') {
      const signUp = clerk.client.signUp;
      if (
        signUp.status === 'missing_requirements' &&
        signUp.verifications.emailAddress.status !== 'unverified'
      ) {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
    }
  } catch (err) {
    throw new Error(clerkMessage(err, 'Could not send the code. Please try again.'));
  }
}

/** Verify the code and sign in to Firebase. Throws with a human message. */
export async function verifyAdminCode(emailRaw: string, code: string): Promise<void> {
  const email = emailRaw.trim().toLowerCase();
  if (emulatorMode) {
    await emulatorSignIn(email, code);
    return;
  }
  const clerk = await getClerk();
  if (!clerk.client) throw new Error('Your sign-in expired. Please request a new code.');

  const signUp = clerk.client.signUp;
  const isSignUpFlow =
    signUp.status === 'missing_requirements' &&
    (signUp.emailAddress ?? '').toLowerCase() === email;

  let sessionId: string | null = null;
  try {
    if (isSignUpFlow) {
      const r = await signUp.attemptEmailAddressVerification({ code });
      sessionId = r.status === 'complete' ? r.createdSessionId : null;
    } else {
      const signIn = clerk.client.signIn;
      if (signIn.status === null) throw new Error('Your sign-in expired. Please request a new code.');
      let needsTransfer = false;
      try {
        const r = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
        if (r.status === 'complete' && r.createdSessionId) sessionId = r.createdSessionId;
        else if (r.firstFactorVerification.status === 'transferable') needsTransfer = true;
      } catch (err) {
        if (clerk.client.signIn.firstFactorVerification.status === 'transferable') needsTransfer = true;
        else throw err;
      }
      if (needsTransfer) {
        const created = await clerk.client.signUp.create({ transfer: true });
        sessionId = created.status === 'complete' ? created.createdSessionId : null;
      }
    }
  } catch (err) {
    throw new Error(clerkMessage(err, 'Invalid code. Check the latest email and try again.'));
  }
  if (!sessionId) throw new Error('Invalid code. Check the latest email and try again.');

  await clerk.setActive({ session: sessionId });
  const clerkJwt = await clerk.session?.getToken();
  if (!clerkJwt) throw new Error('Sign-in could not be completed. Please try again.');

  if (!SYNC_ENDPOINT) throw new Error('Sign-in is not configured (VITE_SYNC_ENDPOINT).');
  const res = await fetch(SYNC_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action: 'clerk_exchange', token: clerkJwt }),
  });
  const data = (await res.json()) as { ok: boolean; token?: string; message?: string };
  if (!data.ok || !data.token) {
    throw new Error(data.message ?? 'Sign-in could not be completed.');
  }
  await signInWithCustomToken(auth, data.token);
  // The Firebase session is the real one — drop Clerk's.
  void clerk.signOut().catch(() => undefined);
}
